<script setup>
import { computed } from 'vue';

import { HrChip } from '../primitives';

/**
 * Input chip — a removable chip representing a selected value, typically inside a multi-select field.
 *
 * With `clickable` the chip carries two independent targets, as M3 specifies: activating the body
 * emits `click` (typically to edit the value), activating the trailing icon emits `remove`.
 *
 * @status recommended
 * @group chips
 * @useWhen Displaying a selected value that the user can remove, e.g. inside HrMultiSelect
 * @useWhen Displaying an applied filter or criterion the user can open to edit and remove separately
 * @avoidWhen A static label is needed — use HrAssistChip or HrSuggestionChip
 * @related HrMultiSelect
 * @a11y The remove button is a separate control with its own focus stop; pass `removeAriaLabel` to
 * name the value it removes, since the default "Remove" is ambiguous beside a clickable body.
 * Both targets activate on Enter and Space.
 */
defineOptions({ name: 'HrInputChip' });

const props = defineProps({
  /**
   * Chip label text. Falls back to the default slot when null.
   * @replacesSlot default
   * @example Design
   */
  label: {
    type: String,
    default: null,
  },
  /**
   * Material icon name shown before the label.
   * @example palette
   */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Chip size.
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /** Makes the chip body a separate target that emits `click`, alongside the remove button. */
  clickable: {
    type: Boolean,
    default: false,
  },
  /** Hides the trailing remove button, for a value that cannot be removed in place. */
  hideRemove: {
    type: Boolean,
    default: false,
  },
  /** Accessible name for the remove button. Name the value being removed, not just "Remove". */
  removeAriaLabel: {
    type: String,
    default: null,
  },
  /** Prevents interaction and dims the chip. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Renders a skeleton placeholder instead of the chip. */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * Quasar props applied in legacy (non-theme) mode, merged over this chip's own defaults.
   * Ignored when a design system theme is active.
   * @deprecated Only for call sites whose legacy appearance must be preserved during migration.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Fired when the user activates the chip body. Only emitted when `clickable` is set. */
  click: null,
  /** Fired when the user activates the remove button. */
  remove: null,
});

/*
 * Grey on grey read as disabled rather than chosen. Named roles rather than a hue, as everywhere
 * else in the design system, so a chip follows the legacy palette instead of restating one colour
 * out of it.
 *
 * @todo Remove legacy-props when Legacy theme is removed
 */
const effectiveLegacyProps = computed(() => ({
  color: 'primary',
  textColor: 'white',
  ...props.legacyProps,
}));
</script>

<template>
  <hr-chip
    variant="input"
    :label="label"
    :icon="icon"
    :size="size"
    :clickable="clickable"
    :removable="!hideRemove"
    :remove-aria-label="removeAriaLabel"
    :disable="disable"
    :pending="pending"
    :legacy-props="effectiveLegacyProps"
    v-bind="$attrs"
    @click="emit('click', $event)"
    @remove="emit('remove')"
  >
    <!-- @slot default Custom chip label content; use instead of the `label` prop for rich content. -->
    <slot />
  </hr-chip>
</template>
